import React from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle2,
  Clock3,
  Mail,
  ShieldCheck,
  Trash2
} from 'lucide-react';

const DeleteAccount = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="bg-[#E6202E] px-6 md:px-10 py-10 text-white">
            <Link to="/" className="inline-block mb-7">
              <img
                src="/ucan-logo-blanco.svg"
                alt="U-CAN"
                className="h-16 w-auto"
                onError={(event) => {
                  event.currentTarget.src = '/ucan-logo-rojo.svg';
                }}
              />
            </Link>

            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-2xl bg-white/15 flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-7 h-7" />
              </div>

              <div>
                <p className="text-sm font-bold uppercase tracking-wider text-white/80 mb-2">
                  Privacidad y control de datos
                </p>

                <h1 className="text-3xl md:text-4xl font-extrabold">
                  Eliminación de cuenta U-CAN
                </h1>

                <p className="mt-3 text-white/90 max-w-2xl">
                  Puedes solicitar la eliminación permanente de tu cuenta y de
                  los datos personales asociados a ella.
                </p>
              </div>
            </div>
          </div>

          <div className="px-6 md:px-10 py-10 space-y-10">
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Cómo solicitar la eliminación
              </h2>

              <div className="space-y-4">
                {[
                  'Envía un correo desde la dirección registrada en tu cuenta de U-CAN.',
                  'Escribe como asunto: “Solicitud de eliminación de cuenta U-CAN”.',
                  'Incluye tu nombre completo, correo registrado y número de teléfono asociado.',
                  'Nuestro equipo verificará la solicitud antes de eliminar definitivamente la cuenta.'
                ].map((step, index) => (
                  <div key={step} className="flex items-start gap-4">
                    <div className="w-9 h-9 rounded-full bg-red-50 text-[#E6202E] font-extrabold flex items-center justify-center flex-shrink-0">
                      {index + 1}
                    </div>

                    <p className="text-gray-700 pt-1.5">{step}</p>
                  </div>
                ))}
              </div>

              <a
                href="mailto:contacto@u-can.cl?subject=Solicitud%20de%20eliminaci%C3%B3n%20de%20cuenta%20U-CAN"
                className="mt-7 inline-flex items-center justify-center gap-2 bg-[#E6202E] hover:bg-red-700 text-white font-bold rounded-xl px-6 py-3 transition-colors"
              >
                <Mail className="w-5 h-5" />
                Solicitar eliminación
              </a>

              <p className="text-sm text-gray-500 mt-3">
                Correo de contacto: contacto@u-can.cl
              </p>
            </section>

            <section className="grid md:grid-cols-2 gap-5">
              <div className="rounded-2xl border border-green-100 bg-green-50 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <CheckCircle2 className="w-6 h-6 text-green-700" />
                  <h2 className="text-xl font-bold text-gray-900">
                    Datos que se eliminarán
                  </h2>
                </div>

                <ul className="space-y-2 text-gray-700">
                  <li>Nombre, correo electrónico y teléfono.</li>
                  <li>Perfil, fotografías e información personal.</li>
                  <li>Información de mascotas asociadas.</li>
                  <li>Solicitudes, propuestas y preferencias.</li>
                  <li>Mensajes y contenido personal almacenado.</li>
                  <li>Datos asociados a la cuenta eliminada.</li>
                </ul>
              </div>

              <div className="rounded-2xl border border-amber-100 bg-amber-50 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <ShieldCheck className="w-6 h-6 text-amber-700" />
                  <h2 className="text-xl font-bold text-gray-900">
                    Datos que podrían conservarse
                  </h2>
                </div>

                <p className="text-gray-700">
                  Cierta información podrá conservarse únicamente cuando sea
                  necesaria para cumplir obligaciones legales, prevenir
                  fraudes, resolver disputas, proteger la seguridad de los
                  usuarios o mantener registros exigidos por la legislación
                  aplicable.
                </p>
              </div>
            </section>

            <section className="rounded-2xl border border-gray-200 p-6">
              <div className="flex items-start gap-4">
                <Clock3 className="w-6 h-6 text-[#E6202E] mt-0.5 flex-shrink-0" />

                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-2">
                    Plazo de procesamiento
                  </h2>

                  <p className="text-gray-700">
                    La solicitud será revisada y procesada dentro de un plazo
                    máximo de 30 días. Es posible que te contactemos para
                    verificar que eres titular de la cuenta antes de realizar
                    la eliminación.
                  </p>
                </div>
              </div>
            </section>

            <section className="rounded-2xl bg-gray-900 text-white p-6">
              <div className="flex items-start gap-4">
                <AlertTriangle className="w-6 h-6 text-yellow-400 mt-0.5 flex-shrink-0" />

                <div>
                  <h2 className="text-xl font-bold mb-2">
                    La eliminación es permanente
                  </h2>

                  <p className="text-gray-300">
                    Una vez eliminada la cuenta, no será posible recuperarla ni
                    restaurar su información. Para volver a utilizar U-CAN,
                    deberás crear una nueva cuenta.
                  </p>
                </div>
              </div>
            </section>

            <div className="pt-2 border-t border-gray-100 text-sm text-gray-500">
              <p>
                Para conocer más sobre el tratamiento de tus datos, revisa
                nuestra{' '}
                <Link
                  to="/privacidad"
                  className="text-[#E6202E] font-semibold hover:underline"
                >
                  Política de Privacidad
                </Link>
                .
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteAccount;
